import { useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';

import { commandPaletteAtom } from 'atoms/commandPaletteAtom';
import { cheatsheetAtom } from 'atoms/cheatsheetAtom';
import { sideNavOpenAtom } from 'atoms/sideNavAtom';
import { themeAtom } from 'atoms/themeAtom';
import { stageFromPath, stageRoute } from 'components/workflowStage';
import { isEditableTarget, nextThemeName } from './shortcutDefs';

// 既存アクションへのグローバルキー割当。新しい副作用・API 呼び出しは作らず、
// すべて既存の route 遷移 / 既存 atom トグルの呼び出し口に過ぎない（C2: カテゴリ b）。
// 入力 / Select / モーダル表示中は単キーを無効化し、ネイティブ入力を阻害しない。
export const useGlobalShortcuts = (): void => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const setPaletteOpen = useSetAtom(commandPaletteAtom);
    const setCheatsheetOpen = useSetAtom(cheatsheetAtom);
    const setSideNavOpen = useSetAtom(sideNavOpenAtom);
    const [theme, setTheme] = useAtom(themeAtom);

    useEffect(() => {
        const moveStage = (delta: number) => {
            const info = stageFromPath(pathname);
            if (!info) return; // ツール外ではステップ移動しない
            const target = Math.min(3, Math.max(0, info.stageIndex + delta));
            if (target !== info.stageIndex) navigate(stageRoute(info.tool, target));
        };

        const onKeyDown = (e: KeyboardEvent) => {
            // ⌘K / Ctrl+K: 修飾キー前提のため入力中でもパレットを開閉する（文字挿入と衝突しない）。
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((o) => !o);
                return;
            }

            // 以降は単キーショートカット。入力中・修飾キー併用・モーダル/ツアー表示中は無効。
            if (isEditableTarget(e.target)) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (document.querySelector('[role="dialog"]')) return;

            switch (e.key) {
                case '?':
                    e.preventDefault();
                    setCheatsheetOpen(true);
                    break;
                case 'b':
                case 'B':
                    e.preventDefault();
                    setSideNavOpen((o) => !o);
                    break;
                case 't':
                case 'T':
                    e.preventDefault();
                    setTheme(nextThemeName(theme));
                    break;
                case '[':
                    e.preventDefault();
                    moveStage(-1);
                    break;
                case ']':
                    e.preventDefault();
                    moveStage(1);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [navigate, pathname, setPaletteOpen, setCheatsheetOpen, setSideNavOpen, setTheme, theme]);
};
